import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from '../Bookings/CurrentSessions.module.css'
import { CgSpinner } from 'react-icons/cg'
import { Link } from 'react-router-dom'
import DateRangeSelector from '../../Components/UI/DateRangeSelector'
import {
    Calendar,
    CalendarDays,
    CheckCircle2,
    Clock3,
    History,
    IndianRupee,
    Package,
    Receipt,
    Store,
    UserRound
} from 'lucide-react'
import { convertDateToDdMmYyyy, convertToAmPm } from '../../Helper/calculations'

const PAGE_SIZE = 20

const toLocalIsoDate = (date) => (
    new Date(date.getTime() - (date.getTimezoneOffset() * 60000)).toISOString().substring(0, 10)
)

const getDefaultDateRange = () => {
    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - 28)

    return {
        startDate: toLocalIsoDate(start),
        endDate: toLocalIsoDate(end)
    }
}

const getSessionKey = (item) => (
    item?.bookingId
    || item?.invoiceNumber
    || `${item?.createdOn || 'unknown'}-${item?.clientName || 'guest'}-${item?.amountToBeCollected || 0}`
)

const CurrentSessions = () => {
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = (() => { try { return (asCtx?.activeStore && asCtx.activeStore.trim() !== "") ? JSON.parse(asCtx.activeStore) : null; } catch { return null; } })();
    const [allSessions, setAllSessions] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [isFetchingMore, setIsFetchingMore] = useState(false)
    const [hasMore, setHasMore] = useState(true)
    const [currentCursor, setCurrentCursor] = useState(0)
    const [hasLoadedInitialChunk, setHasLoadedInitialChunk] = useState(false)
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)
    const [dates, setDates] = useState(getDefaultDateRange)
    const scrollContainerRef = useRef(null)
    const loadMoreRef = useRef(null)
    const allSessionsRef = useRef([])
    const currentCursorRef = useRef(0)
    const hasMoreRef = useRef(true)
    const isFetchingMoreRef = useRef(false)

    useEffect(() => {
        allSessionsRef.current = allSessions
    }, [allSessions])

    useEffect(() => {
        currentCursorRef.current = currentCursor
    }, [currentCursor])

    const closeDateModal = () => {
        setIsDatePickerOpen(false)
    }

    const changeDateHandler = (startDate, endDate) => {
        setDates({ startDate, endDate })
    }

    const getCurrentSessions = useCallback(async ({ reset = false } = {}) => {
        if (!activeStore?.storeIdentifier) {
            return
        }

        if (!reset && (!hasMoreRef.current || isFetchingMoreRef.current)) {
            return
        }

        if (reset) {
            setIsLoading(true)
            setHasMore(true)
            setHasLoadedInitialChunk(false)
            setCurrentCursor(0)
            currentCursorRef.current = 0
            hasMoreRef.current = true
            isFetchingMoreRef.current = true
        } else {
            setIsFetchingMore(true)
            isFetchingMoreRef.current = true
        }

        const requestCursor = reset ? 0 : currentCursorRef.current

        const getCurrentSessionsResponse = await fetch(BASE_URL_API + 'getBookingHistory', {
            method: 'POST',
            body: JSON.stringify({
                token: authCtx.token,
                storeIdentifier: activeStore?.storeIdentifier,
                lastId: requestCursor,
                limit: PAGE_SIZE,
                startDate: dates.startDate,
                endDate: dates.endDate
            })
        })

        if (!getCurrentSessionsResponse.ok) {
            console.log('Something went wrong : Server Error')
        } else {
            const getCurrentSessionsRespo = await getCurrentSessionsResponse.json()
            if (getCurrentSessionsRespo.status === 'success') {
                const incomingSessions = Array.isArray(getCurrentSessionsRespo.response)
                    ? getCurrentSessionsRespo.response
                    : []
                const resolvedNextCursor = Number(getCurrentSessionsRespo?.nextCursor || 0)

                const baseRecords = reset ? [] : allSessionsRef.current
                const existingKeys = new Set(baseRecords.map(getSessionKey))
                const uniqueIncoming = incomingSessions.filter((item) => {
                    const key = getSessionKey(item)
                    if (existingKeys.has(key)) {
                        return false
                    }
                    existingKeys.add(key)
                    return true
                })

                setAllSessions([...baseRecords, ...uniqueIncoming])

                setCurrentCursor(resolvedNextCursor)
                currentCursorRef.current = resolvedNextCursor

                const canLoadMore = resolvedNextCursor > 0 && resolvedNextCursor !== requestCursor && uniqueIncoming.length > 0
                setHasMore(canLoadMore)
                hasMoreRef.current = canLoadMore
            } else if (
                getCurrentSessionsRespo.status === 'failed'
                && getCurrentSessionsRespo.message === 'No records found'
            ) {
                setHasMore(false)
                hasMoreRef.current = false
            } else if (getCurrentSessionsRespo.message === 'tokenExpired') {
                authCtx.logout()
            }
            console.log(getCurrentSessionsRespo)
        }
        if (reset) {
            setHasLoadedInitialChunk(true)
        }
        setIsLoading(false)
        setIsFetchingMore(false)
        isFetchingMoreRef.current = false
    }, [activeStore?.storeIdentifier, authCtx.logout, authCtx.token, dates.endDate, dates.startDate])

    useEffect(() => {
        if (activeStore !== null) {
            setAllSessions([])
            setCurrentCursor(0)
            setHasLoadedInitialChunk(false)
            currentCursorRef.current = 0
            setHasMore(true)
            hasMoreRef.current = true
            getCurrentSessions({ reset: true })
        }
    }, [activeStore?.storeIdentifier, dates.endDate, dates.startDate, getCurrentSessions])
    const selectedRangeLabel = useMemo(() => {
        return `${dates.startDate} → ${dates.endDate}`
    }, [dates.endDate, dates.startDate])


    useEffect(() => {
        if (!loadMoreRef.current || !scrollContainerRef.current || isLoading || isFetchingMore || !hasMore) {
            return undefined
        }

        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries
                if (entry?.isIntersecting) {
                    getCurrentSessions()
                }
            },
            {
                root: scrollContainerRef.current,
                rootMargin: '0px 0px 320px 0px',
                threshold: 0.05
            }
        )

        observer.observe(loadMoreRef.current)

        return () => observer.disconnect()
    }, [getCurrentSessions, hasMore, isFetchingMore, isLoading])


    const summary = useMemo(() => {
        return allSessions.reduce((accumulator, item) => {
            accumulator.totalBookings += 1
            accumulator.totalRevenue += Number(item?.amountToBeCollected || 0)
            accumulator.totalItems += Number(item?.totalItems || 0)
            return accumulator
        }, {
            totalBookings: 0,
            totalRevenue: 0,
            totalItems: 0
        })
    }, [allSessions])

    if (activeStore === null) {
        return (
            <div className={styles.mainWrapper}>
                <div className={styles.emptyStateCard}>
                    <div className={styles.emptyStateIcon}>
                        <Store size={18} />
                    </div>
                    <h3>Select a store to view booking history</h3>
                    <p>Once a store is active, completed sessions and billing history appear here.</p>
                </div>
            </div>
        )
    }

    return (
        <>
        <div className={styles.mainWrapper} ref={scrollContainerRef}>
            <div className={styles.mainHeader}>
                <div className={styles.mainTitleBlock}>
                    <span className={styles.statusPill}>Booking History</span>
                    <p className={styles.mainSubtitle}>Track finalized bookings, billed services, and collected revenue in one compact view.</p>
                </div>

                <div className={styles.headerStats}>
                    <div className={styles.statChip}>
                        <Receipt size={14} />
                        <span>{summary.totalBookings} bookings</span>
                    </div>
                    <div className={styles.statChip}>
                        <Package size={14} />
                        <span>{summary.totalItems} billed items</span>
                    </div>
                    <button
                        className={styles.dateRangeBtn}
                        onClick={() => setIsDatePickerOpen(true)}
                        type="button"
                    >
                        <Calendar size={14} />
                        <span>{selectedRangeLabel}</span>
                    </button>
                </div>
            </div>

            {(isLoading || !hasLoadedInitialChunk) ? (
                <div className={styles.loaderWrapper}>
                    <div className={styles.loaderIconWrap}>
                        <CgSpinner size={22} className={styles.spinner} />
                    </div>
                    <div className={styles.loaderTitle}>Fetching booking history...</div>
                    <div className={styles.loaderSubtitle}>Syncing completed sessions for the selected store.</div>
                </div>
            ) : (
                <div className={styles.mainBody}>
                    <div className={styles.metaBar}>
                        <div className={styles.metaHint}>
                            <History size={14} />
                            <span>{allSessions.length > 0 ? `${allSessions.length} records loaded` : 'No records available yet'}</span>
                        </div>
                        <div className={styles.metaHint}>
                            <CheckCircle2 size={14} />
                            <span>{hasMore ? 'More history loads as you scroll' : 'Only completed bookings are listed'}</span>
                        </div>
                    </div>

                    {allSessions.length === 0 ? (
                        <div className={styles.emptyStateCard}>
                            <div className={styles.emptyStateIcon}>
                                <History size={18} />
                            </div>
                            <h3>No completed bookings yet</h3>
                            <p>When appointments are billed and finished, they will appear here automatically.</p>
                        </div>
                    ) : (
                        <div className={styles.eachSectionWrapper}>
                            {allSessions.map((item, index) => (
                                <a
                                    key={item?.bookingId || index}
                                    href={`/booking/${item?.bookingId}`}
                                    target="_blank"
                                    className={styles.mainItemWrapper}
                                >
                                    <div className={styles.cardTopRow}>
                                        <div className={styles.bookingIdentity}>
                                            <span className={styles.bookingId} title={`Booking Id is : ${item?.bookingId}`}>
                                                #{item?.invoiceNumber?.slice(-5) || item?.bookingId?.slice(-5) || '-----'}
                                            </span>
                                            <span className={styles.dateBadge}>
                                                <CalendarDays size={12} />
                                                {convertDateToDdMmYyyy(item?.createdOn?.substring(0, 10))}
                                            </span>
                                            <span className={styles.statusBadge}>
                                                <CheckCircle2 size={12} /> Completed
                                            </span>
                                        </div>
                                        <div className={styles.amountBlock}>
                                            <div className={styles.total}>
                                                ₹{Number(item?.amountToBeCollected || 0).toLocaleString('en-IN')}
                                            </div>
                                            <div className={styles.itemsInline}>
                                                <Package size={12} />
                                                <span>{item?.totalItems > 0 ? `${item?.totalItems} item(s)` : 'No items'}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className={styles.clientName}>
                                        <div className={styles.userIcon}>
                                            <UserRound size={15} />
                                        </div>
                                        <div className={styles.clientTextWrap}>
                                            <div className={styles.clientPrimary}>{item?.clientName || `NA`}</div>
                                            <div className={styles.clientSecondaryRow}>
                                                <span className={styles.clientSecondary}>Booking reference #{item?.bookingId || 'NA'}</span>
                                                <span className={styles.timeInline}>
                                                    <Clock3 size={12} />
                                                    {convertToAmPm(item?.createdOn?.substring(11, 19))}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </a>
                            ))}

                            {hasMore && (
                                <div ref={loadMoreRef} className={styles.loadMoreTrigger}>
                                    {isFetchingMore ? (
                                        <div className={styles.loadMoreState}>
                                            <CgSpinner size={15} className={styles.spinner} />
                                            <span>Loading more history...</span>
                                        </div>
                                    ) : (
                                        <div className={styles.loadMoreHint}>Scroll to load more bookings</div>
                                    )}
                                </div>
                            )}

                            {!hasMore && allSessions.length > 0 && (
                                <div className={styles.thatsAll}>
                                    End of booking history
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
        <DateRangeSelector
            isOpen={isDatePickerOpen}
            closeModal={closeDateModal}
            changeDateHandler={changeDateHandler}
            initialStartDate={dates.startDate}
            initialEndDate={dates.endDate}
        />
        </>
    )
}

export default CurrentSessions